/**
 * Keyboard Navigation Hook
 * Provides shortcuts for navigating the Workshop Recall interface
 */

import { useEffect, useCallback } from 'react';

export interface KeyboardShortcuts {
  // Navigation
  onNext?: () => void;           // Right arrow, N
  onPrevious?: () => void;       // Left arrow, P
  onFirst?: () => void;          // Home
  onLast?: () => void;           // End

  // Actions
  onEscape?: () => void;         // Escape - close modal, exit mode
  onEnter?: () => void;          // Enter - select, confirm
  onSpace?: () => void;          // Space - pause/play in showcase

  // Modes
  onSearch?: () => void;         // S, / - focus search
  onShowcase?: () => void;       // F - fullscreen/showcase
  onCompare?: () => void;        // C - compare mode
  onHome?: () => void;           // H - reset to home
  onHelp?: () => void;           // ? - show help

  // Image selection by number (1-9)
  onSelectNumber?: (num: number) => void;

  // Zoom
  onZoomIn?: () => void;         // + or =
  onZoomOut?: () => void;        // -

  // Voice (PTT - push to talk)
  onVoiceActivate?: () => void;    // V down - start listening
  onVoiceDeactivate?: () => void;  // V up - stop listening
}

export function useKeyboardShortcuts(shortcuts: KeyboardShortcuts, enabled = true) {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Ignore key repeats for PTT
    if (event.repeat) return;
    // Don't handle if user is typing in an input
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      // Only allow Escape in inputs
      if (event.key !== 'Escape') return;
    }

    // Prevent default for handled keys
    const handled = (() => {
      switch (event.key) {
        case 'ArrowRight':
        case 'n':
        case 'N':
          shortcuts.onNext?.();
          return !!shortcuts.onNext;

        case 'ArrowLeft':
        case 'p':
        case 'P':
          shortcuts.onPrevious?.();
          return !!shortcuts.onPrevious;

        case 'Home':
          shortcuts.onFirst?.();
          return !!shortcuts.onFirst;

        case 'End':
          shortcuts.onLast?.();
          return !!shortcuts.onLast;

        case 'Escape':
          shortcuts.onEscape?.();
          return !!shortcuts.onEscape;

        case 'Enter':
          shortcuts.onEnter?.();
          return !!shortcuts.onEnter;

        case ' ':
          shortcuts.onSpace?.();
          return !!shortcuts.onSpace;

        case 's':
        case 'S':
        case '/':
          shortcuts.onSearch?.();
          return !!shortcuts.onSearch;

        case 'f':
        case 'F':
          shortcuts.onShowcase?.();
          return !!shortcuts.onShowcase;

        case 'c':
        case 'C':
          shortcuts.onCompare?.();
          return !!shortcuts.onCompare;

        case 'h':
        case 'H':
          shortcuts.onHome?.();
          return !!shortcuts.onHome;

        case '?':
          shortcuts.onHelp?.();
          return !!shortcuts.onHelp;

        case 'v':
        case 'V':
          shortcuts.onVoiceActivate?.();
          return !!shortcuts.onVoiceActivate;

        case '+':
        case '=':
          shortcuts.onZoomIn?.();
          return !!shortcuts.onZoomIn;

        case '-':
          shortcuts.onZoomOut?.();
          return !!shortcuts.onZoomOut;

        default:
          // Number keys 1-9 for image selection
          if (event.key >= '1' && event.key <= '9') {
            const num = parseInt(event.key, 10);
            shortcuts.onSelectNumber?.(num);
            return !!shortcuts.onSelectNumber;
          }
          return false;
      }
    })();

    if (handled) {
      event.preventDefault();
    }
  }, [shortcuts]);

  // Handle keyup for PTT release
  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    // V key release - stop PTT
    if (event.key === 'v' || event.key === 'V') {
      shortcuts.onVoiceDeactivate?.();
    }
  }, [shortcuts]);

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp, enabled]);
}

/**
 * Get keyboard shortcuts help text
 */
export const keyboardShortcutsHelp = [
  { keys: ['←', '→'], action: 'Navigate images' },
  { keys: ['1-9'], action: 'Select image by number' },
  { keys: ['Esc'], action: 'Close / Go back' },
  { keys: ['Enter'], action: 'Select / Confirm' },
  { keys: ['Space'], action: 'Pause/Play showcase' },
  { keys: ['S', '/'], action: 'Focus search' },
  { keys: ['H'], action: 'Reset to home' },
  { keys: ['F'], action: 'Toggle fullscreen/showcase' },
  { keys: ['C'], action: 'Compare mode' },
  { keys: ['V'], action: 'Ask Ventura (push-to-talk)' },
  { keys: ['?'], action: 'Show help' },
];
